import type { CountryProfile } from '@/types';

// ---------------------------------------------------------------------------
// REST Countries API — free, no-auth, CORS OK
// Covers all 195 UN member states + territories
// Cache: 24h localStorage
// ---------------------------------------------------------------------------

const CACHE_KEY = 'detectx-rest-countries-v2';
const CACHE_TTL = 24 * 60 * 60 * 1000;

interface RestCountry {
  cca2: string;
  name: { common: string };
  capital?: string[];
  flag: string;
  region: string;
  subregion?: string;
  languages?: Record<string, string>;
  currencies?: Record<string, { name: string; symbol: string }>;
  borders?: string[];
  latlng: [number, number]; // [lat, lon]
  area: number;
  population: number;
}

// ---------------------------------------------------------------------------
// Static GDP table: [gdpBillionUSD, gdpPerCapitaUSD] — approx. 2024
// ---------------------------------------------------------------------------
const GDP_TABLE: Record<string, [number, number]> = {
  US:[28780,85373], CN:[18530,13200], DE:[4500,54000],  JP:[4200,33800],
  IN:[3850,2700],   GB:[3100,46000],  FR:[3100,46000],   IT:[2300,38000],
  BR:[2200,10600],  CA:[2200,57900],  RU:[1900,13600],   KR:[1700,33000],
  AU:[1700,65000],  MX:[1800,13800],  ES:[1700,34000],   ID:[1371,4900],
  NL:[1160,65000],  SA:[1100,28500],  TR:[1100,12800],   CH:[900,100000],
  TW:[790,33000],   PL:[730,19000],   SE:[600,57000],    BE:[600,50000],
  AR:[640,13900],   NO:[560,97000],   TH:[540,7500],     ZA:[380,6200],
  PH:[430,3700],    EG:[490,4400],    MY:[420,12700],    VN:[430,4300],
  AE:[530,53000],   NG:[400,1700],    SG:[500,84000],    IL:[520,53000],
  PK:[350,1500],    IR:[400,4700],    UA:[180,4900],     BD:[450,2700],
  MM:[70,1300],     QA:[220,76000],   KZ:[260,12800],    KW:[150,34000],
  OM:[105,22000],   BH:[45,30000],    DZ:[220,4800],     MA:[140,3700],
  TN:[47,3800],     LY:[50,7100],     GH:[80,2400],      KE:[110,2000],
  ET:[160,1300],    TZ:[80,1300],     CI:[75,2700],      SN:[30,1700],
  CM:[45,1600],     AO:[92,2700],     MZ:[20,600],       ZM:[27,1400],
  ZW:[28,1700],     BW:[22,8900],     NA:[14,5400],      RW:[14,1100],
  UG:[52,1100],     SD:[45,1000],     MG:[16,600],       MU:[14,11000],
  LK:[84,3800],     NP:[43,1400],     AF:[14,390],       IQ:[264,6000],
  SY:[40,2000],     LB:[18,2600],     JO:[50,4600],      YE:[20,600],
  CU:[100,8800],    VE:[100,3500],    CO:[360,6900],     CL:[320,16000],
  PE:[270,7700],    EC:[120,6400],    BO:[45,3800],      PY:[45,6200],
  UY:[80,22000],    GT:[95,5100],     HN:[35,3400],      SV:[32,4700],
  NI:[15,2200],     CR:[68,13000],    PA:[75,16400],     DO:[120,10700],
  HT:[20,1600],     JM:[18,6100],     TT:[26,18000],     PT:[270,26000],
  GR:[240,23000],   CZ:[330,31000],   HU:[210,22000],    RO:[350,18000],
  SK:[130,24000],   FI:[280,50000],   DK:[400,67000],    AT:[480,53000],
  IE:[520,100000],  HR:[80,20000],    BG:[100,15000],    RS:[75,11000],
  SI:[66,31000],    LT:[77,28000],    LV:[44,24000],     EE:[38,28000],
  LU:[80,130000],   CY:[30,33000],    MT:[17,35000],     IS:[30,80000],
  AL:[22,8000],     MK:[14,7800],     BA:[25,7500],      ME:[6,9800],
  MD:[15,4200],     GE:[28,7500],     AM:[22,7400],      AZ:[72,7000],
  BY:[75,8000],     UZ:[100,2800],    TM:[50,8500],      TJ:[12,1300],
  KG:[12,1700],     MN:[20,5700],     KH:[32,1900],      LA:[16,2200],
  BN:[15,33000],    TL:[3,2300],      PG:[30,3100],      FJ:[5,5300],
  NZ:[250,48000],   GQ:[15,10000],    GA:[21,9300],      CG:[15,3400],
  CD:[65,700],      TD:[12,700],      NE:[17,570],       ML:[19,1000],
  BF:[20,1000],     GN:[21,2000],     SL:[5,600],        LR:[4,800],
  GM:[2,900],       MR:[10,2300],     CV:[2,4100],       DJ:[4,3500],
  ER:[3,650],       BI:[3,250],       MW:[14,700],       LS:[3,1100],
  SZ:[5,4600],      KM:[1,1500],      SS:[10,700],       SO:[10,600],
  CF:[3,500],       ST:[0.5,2200],    GW:[2,1700],       KP:[40,1700],
  PS:[20,4000],     BT:[3,4100],      MV:[6,14000],      SC:[2,19000],
  SB:[2,2200],      VU:[1,3200],       WS:[1,4100],
  TO:[0.5,4500],    NR:[0.1,10000],   PW:[0.3,15000],    FM:[0.4,3700],
  MH:[0.3,3600],    KI:[0.2,1700],    TV:[0.06,6000],    PF:[6,19000],
};

function getGdp(code: string): [number, number] {
  return GDP_TABLE[code] ?? [0, 0];
}

// ---------------------------------------------------------------------------
// Cache helpers
// ---------------------------------------------------------------------------
function readCache(): RestCountry[] | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { ts, data } = JSON.parse(raw) as { ts: number; data: RestCountry[] };
    if (Date.now() - ts > CACHE_TTL) return null;
    return data;
  } catch { return null; }
}

function writeCache(data: RestCountry[]): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch { /* storage full — skip */ }
}

// ---------------------------------------------------------------------------
// Convert RestCountry → CountryProfile (basic profile for non-static countries)
// ---------------------------------------------------------------------------
export function toBasicProfile(rc: RestCountry): CountryProfile {
  const [gdpB, gdpPc] = getGdp(rc.cca2);
  return {
    code: rc.cca2,
    name: rc.name.common,
    nameKo: rc.name.common,
    flagEmoji: rc.flag,
    capitalCity: rc.capital?.[0] ?? '—',
    capitalLon: rc.latlng[1] ?? 0,
    capitalLat: rc.latlng[0] ?? 0,
    population: Math.max(1, Math.round(rc.population / 1_000_000)),
    gdpBillionUSD: gdpB,
    gdpPerCapita: gdpPc,
    gdpGrowthPct: 0,
    military: {
      activeTroops: 0, reserveTroops: 0, aircraftCarriers: 0,
      submarines: 0, destroyersFrigates: 0, combatAircraft: 0,
      tanks: 0, artilleryPieces: 0, navalVesselsTotal: 0,
      budgetBillionUSD: 0, globalFirepowerRank: 0,
    },
    nuclearStatus: 'none',
    leader: { name: '—', title: '—', sinceYear: 0, age: 0, riskLevel: 'low', riskNotes: '', successionRisk: 'low' },
    alliances: [],
    geopoliticalStance: 'neutral',
    strategicObjectives: [],
    instabilityRisk: 'low',
    powerIndex: 0,
    // REST Countries metadata
    source: 'rest-countries',
    _region: [rc.region, rc.subregion].filter(Boolean).join(' › '),
    _languages: rc.languages ? Object.values(rc.languages).slice(0, 4).join(', ') : '—',
    _currencies: rc.currencies
      ? Object.values(rc.currencies).map((c) => `${c.name} (${c.symbol})`).slice(0, 2).join(', ')
      : '—',
    _area: Math.round(rc.area ?? 0),
    _borders: rc.borders ?? [],
  };
}

// ---------------------------------------------------------------------------
// Main fetch — returns map of ISO-2 → basic CountryProfile for ALL countries
// ---------------------------------------------------------------------------
export async function fetchRestCountries(): Promise<Map<string, CountryProfile>> {
  let raw = readCache();

  if (!raw) {
    try {
      // REST Countries v3.1 폐기(v5는 유료 키 필수) → 자체 정적 데이터 사용
      // (mledoze/countries + World Bank 인구 병합, public/data/countries.json)
      const res = await fetch('/data/countries.json', { signal: AbortSignal.timeout(15_000) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      raw = (await res.json()) as RestCountry[];
      writeCache(raw);
    } catch (err) {
      console.warn('[RestCountries] fetch failed:', err);
      return new Map();
    }
  }

  const map = new Map<string, CountryProfile>();
  for (const rc of raw) {
    if (rc.cca2 && Array.isArray(rc.latlng) && rc.latlng.length === 2) {
      map.set(rc.cca2, toBasicProfile(rc));
    }
  }
  return map;
}
